import DownArrow from 'assets/Images/Common/down-arrow.png';
import { FC, useEffect, useState } from 'react';
import {
  createIntegration,
  validateIntegration,
} from 'services/apiHandlers/Integration/CartIntegration';
import * as Yup from 'yup';
import { useFormik, FormikValues } from 'formik';
import uuid from 'react-uuid';
import { Toastify } from 'App';
import { z } from 'zod';
import React from "react";

const validateBigCommerceLegacySchema = z.object({
  apiToken: z.string(),
  id: z.string().optional(),
  shouldGrabOrderHistory: z.boolean(),
  storeId: z.string(),
  storeUrl: z.string(),
  uniqueIdConstraint: z.string(),
  username: z.string(),
  _type: z.string(),
});
type validateBigCommerceLegacy = z.infer<
  typeof validateBigCommerceLegacySchema
>;

const validateResponseSchema = z.object({
  status: z.number(),
});
type validateResponse = z.infer<typeof validateResponseSchema>;

interface Props {
  removeBigCommerce: () => void;
  getData: () => void;
  data?: validateBigCommerceLegacy | undefined;
}

const radioOptions = [
  {
    title: 'store + orderId combination',
    id: '1',
    defaultChecked: true,
    value: 'composite',
  },
  { title: 'orderId only', id: '2', value: 'single' },
];

const bigCommerceDataLegacyData = [
  { name: 'storeUrl', placeholder: 'Store Url' },
  { name: 'username', placeholder: 'Username' },
  { name: 'apiToken', placeholder: 'Api Token' },
];

const BigCommerceLegacyForm: FC<Props> = ({
  removeBigCommerce,
  data,
  getData,
}) => {
  const [showOptions, setShowOptions] = useState<boolean>(true);
  const [invalidCredentials, setInvalidCredentials] = useState<boolean>(false);
  const [isValidData, setIsValidData] = useState<boolean>(false);

  const formik: FormikValues = useFormik({
    enableReinitialize: true,
    initialValues: {
      apiToken: '',
      storeUrl: '',
      username: '',
      uniqueIdConstraint: 'composite',
      shouldGrabOrderHistory: true,
    },
    validationSchema: Yup.object({
      storeUrl: Yup.string().required('Required'),
      apiToken: Yup.string().required('Required'),
      username: Yup.string().required('Required'),
    }),
    onSubmit: async (values, { resetForm }) => {
      const data = {
        ...values,
        id: uuid(),
        _type: 'bigcommerce_legacy',
        storeId: 'null',
      };
      try {
        const createBigCommerceLegacyResponse = (await createIntegration(
          data
        )) as validateResponse;
        if (createBigCommerceLegacyResponse.status === 200) {
          setIsValidData(false);
          setInvalidCredentials(false);
          getData();
          Toastify(
            'Big Commerce Legacy Created Successfullly',
            'success',
            'bigCommerceLegacy1'
          );
          resetForm();
        }
      } catch (error) {
        Toastify('Big Commerce Creation Failed', 'error', 'bigCommerceLegacy2');
      }
    },
  });

  const checkValidation = async () => {
    {
      if (
        formik.values.apiToken.length > 0 &&
        formik.values.storeUrl.length > 0 &&
        formik.values.username.length > 0
      ) {
        const data = {
          ...formik.values,
          id: '72bb46a3-f88d-4b30-8825-06022aa5d4d0',
          _type: 'bigcommerce_legacy',
          storeId: 'null',
        };
        try {
          const validateBigCommerceLegacyResponse = (await validateIntegration(
            data
          )) as validateResponse;
          if (validateBigCommerceLegacyResponse.status === 200) {
            setIsValidData(true);
          } else {
            setInvalidCredentials(true);
          }
        } catch (error) {
          console.log(error);
        }
      }
    }
  };

  useEffect(() => {
    const checkInputValidation = setTimeout(() => {
      checkValidation();
    }, 3000);
    return () => clearTimeout(checkInputValidation);
  }, [formik.values]);

  useEffect(() => {
    data ? formik.setValues(data) : formik.setValues(formik.initialValues);
  }, []);

  return (
    <div>
      <form
        className="bg-white mt-6 p-4 bg-[#f5f5f5] border dark:border-[#fff] rounded  dark:bg-[#41464E] "
        onSubmit={formik.handleSubmit}
      >
        <h4 className="text-13 font-semibold leading-4 text-black-700 dark:text-white mb-3">
          Big Commerce Legacy
        </h4>
        {bigCommerceDataLegacyData.map((input) => (
          <div key={input.name}>
            <input
              className="w-full focus:border-primary dark:focus:border-white  placeholder:text-gray-500 focus:outline-none text-13 font-medium leading-3 text-gray-700 dark:text-white py-2 rounded px-4 bg-white  dark:bg-[#41464E]  border border-[#ced4da] dark:border-[#fff] sm:[38px] h-9 mt-4"
              type="text"
              placeholder={input.placeholder}
              name={input.name}
              value={formik.values[input.name]}
              onChange={formik.handleChange}
            />
          </div>
        ))}
        <div className="relative flex items-center cursor-pointer mt-4">
          <input
            id="checkBox1"
            className="cursor-pointer"
            name="shouldGrabOrderHistory"
            value="checked"
            type="checkbox"
            defaultChecked
            onChange={formik.handleChange}
          />
          <label
            className="checkBox relative cursor-pointer pl-2"
            htmlFor="checkBox1"
          >
            <span className="text-13 font-normal leading-3 text-black-700 dark:text-white">
              Grab Order History (Last 2 year Orders data)
            </span>
          </label>
        </div>
        <div>
          <div
            onClick={() => setShowOptions((prev) => !prev)}
            className="inline-flex items-center relative mt-4 cursor-pointer"
          >
            <p className="text-xs font-semibold leading-4 text-black-700 dark:text-white">
              Advance Configuration Options
            </p>
            <img className="pl-2" src={DownArrow} alt="Arrow" />
          </div>
          {showOptions && (
            <>
              <p className="text-xs font-medium leading-3 text-primary dark:text-white mt-4">
                Unique OrderId Constraint:
              </p>
              {radioOptions.map((option) => (
                <div
                  key={option.id}
                  className="radio-btn block relative flex items-center text-13 font-normal leading-3 text-[#495057] dark:text-[#CED4DA]  dark:text-white dark:text-white mt-4"
                >
                  <input
                    id={option.title}
                    type="radio"
                    value={option.value}
                    name="uniqueIdConstraint"
                    className="w-4 h-4 text-primary bg-gray-100 border-gray-300 dark:ring-offset-gray-800  dark:bg-gray-700 dark:border-gray-600 mr-2"
                    defaultChecked={option.defaultChecked}
                    checked={formik.values.uniqueIdConstraint === option.value}
                    onChange={formik.handleChange}
                  />
                  <label
                    htmlFor={option.title}
                    className="text-xs font-semibold leading-3 tracking-wide uppercase text-[#495057] dark:text-[#CED4DA]  dark:text-white dark:text-white inline-block inline-block"
                  >
                    {option.title}
                  </label>
                </div>
              ))}
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center mt-4">
          <button
            type="submit"
            className={` ${
              !isValidData
                ? 'bg-gray-800 text-[#495057] dark:text-[#CED4DA]  dark:text-white font-nomral '
                : 'bg-primary text-white '
            }text-13 font-medium leading-5 rounded py-2 px-4 dark:hover:bg-white dark:hover:text-[#495057] dark:text-[#CED4DA]  dark:text-white mr-4 mt-4`}
            disabled={!isValidData}
          >
            Save
          </button>
          <button
            type="button"
            className="text-13 font-medium leading-5 rounded py-2 px-4 text-white bg-[#B31E33] dark:hover:bg-white dark:hover:text-[#b31e33] mr-4 mt-4"
            onClick={removeBigCommerce}
          >
            Remove
          </button>
          <a
            
            className="text-sm leading-4 text-[#cb5b57] dark:text-white mt-4"
          >
            {invalidCredentials
              ? 'invalid Credentials'
              : !isValidData
              ? 'All fields are required'
              : ''}
          </a>
        </div>
      </form>
    </div>
  );
};
export default BigCommerceLegacyForm;
